import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spin, message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useModal from 'hooks/useModal';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import dayjs from 'lib/dayjs';
import { pureLLM } from 'services/queries/pureLLM';
import {
  updateOfficialScenarioPolicy,
  updateOfficialScenarioSubPolicy,
} from 'services/api/pureLLM';
import { mapPolicies, getScenarioGroupTimeValue } from './Hooks/useOfficialScenarioData';
import { useResolveTodoTypeLabel } from './Hooks/useResolveTodoTypeLabel';
import DetailHeader from './OfficialScenario/Components/ScenarioDetail/DetailHeader';
import RuleStatusBar from './OfficialScenario/Components/ScenarioDetail/RuleStatusBar';
import BranchCard, {
  BranchCardData,
  BranchCardProps,
} from './OfficialScenario/Components/ScenarioDetail/BranchCard';
import FeedbackModal from './OfficialScenario/Components/ScenarioDetail/FeedbackModal';
import ReceptionModeDrawer from './OfficialScenario/Components/ScenarioDetail/ReceptionModeDrawer';
import type { ApiReceptionMode } from './Types/api';

interface FeedbackMeta {
  presetReply: string;
}

interface ReceptionModeMeta {
  subPolicyId?: string;
  branchName: string;
  triggerConditions: BranchCardProps['triggerConditions'];
  replyLogicDesc: string;
  replyExample: string;
  fixedReplyContent?: string;
  isFixedReply: boolean;
  currentMode: ApiReceptionMode;
  originalReceptionMode?: ApiReceptionMode;
  todoLabel?: string;
  todoType?: string;
  storeScope?: BranchCardProps['storeScope'];
}

function normalizeScenarioId(id?: string | number) {
  if (id == null) return '';
  if (typeof id === 'number') return id;
  return /^\d+$/.test(id) ? Number(id) : id;
}

export interface ScenarioDetailContentProps {
  enabled: boolean;
  /** 站点 */
  region?: string;
  /** 店铺 ID（传给 detail 接口） */
  storeId?: string;
  /** 店铺分组 ID */
  storeGroupId?: string;
  /** 场景 intentId（来自 llmIntentId） */
  intentId?: string;
  /** 命中的策略 policyId（用于定位） */
  policyId?: string;
  /** 命中的子策略 subPolicyId（用于定位） */
  subPolicyId?: string;
  /** Modal 的 z-index，用于内嵌抽屉 */
  zIndex?: number;
  /** 店铺分组名称（用于 ReceptionModeDrawer） */
  storeGroupName?: string;
  /** 隐藏 DetailHeader 的标题栏（弹窗模式下由外层 title 代替） */
  hideDetailTitleBar?: boolean;
  /** 是否允许编辑 */
  editable?: boolean;
  /** 搜索关键词 */
  keyword?: string;
}

export interface ScenarioDetailHeaderProps {
  enabled?: boolean;
  region?: string;
  storeId?: string;
  storeGroupId?: string;
  intentId?: string;
  keyword?: string;
}

export function ScenarioDetailHeader({
  enabled = true,
  region,
  storeId,
  storeGroupId,
  intentId,
  keyword,
}: ScenarioDetailHeaderProps) {
  const { t } = useTranslate();
  const language = useSystemLanguageValue();

  const { data: scenario } = useQuery({
    ...pureLLM.scenarioDetail({
      region,
      storeId,
      storeGroupId,
      keyword,
      language,
      intentId: intentId ?? '',
    }),
    enabled: enabled && !!intentId,
  });

  if (!scenario) return null;

  const normalizedUpdateTime = getScenarioGroupTimeValue(scenario.updateTime);
  const formattedUpdateTime = normalizedUpdateTime
    ? dayjs(normalizedUpdateTime).format('YYYY/MM/DD HH:mm:ss')
    : '';

  return (
    <div className="flex items-center gap-3 min-w-0">
      <h3 className="m-0 text-base font-medium text-gray-800">
        {scenario.groupName} - {scenario.name}
      </h3>
      {formattedUpdateTime ? (
        <span
          className="whitespace-nowrap"
          style={{ fontWeight: 400, fontSize: 12, color: '#B5B9BF', lineHeight: '18px' }}
        >
          {t('scene.aiAgentConfig.scenarioDetail.label.updateTime')}:{formattedUpdateTime}
        </span>
      ) : null}
    </div>
  );
}

function findBranchPathByPolicyId(
  branches: BranchCardData[],
  targetPolicyId: string,
  parentPath: BranchCardData[] = [],
): BranchCardData[] | null {
  for (const branch of branches) {
    const currentPath = [...parentPath, branch];
    if (
      String(branch.policyId) === String(targetPolicyId) ||
      String(branch.togglePolicyId) === String(targetPolicyId)
    ) {
      return currentPath;
    }
    if (branch.children?.length) {
      const childPath = findBranchPathByPolicyId(branch.children, targetPolicyId, currentPath);
      if (childPath) {
        return childPath;
      }
    }
  }

  return null;
}

function getScrollParent(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement;
  while (current) {
    const { overflow, overflowY } = window.getComputedStyle(current);
    if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

function getOffsetTopWithinContainer(target: HTMLElement, container: HTMLElement): number {
  let current: HTMLElement | null = target;
  let offsetTop = 0;

  while (current && current !== container) {
    offsetTop += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }

  return offsetTop;
}

export function ScenarioDetailContent({
  enabled,
  region,
  storeId,
  storeGroupId,
  storeGroupName,
  intentId,
  policyId,
  subPolicyId,
  zIndex,
  hideDetailTitleBar,
  editable = true,
  keyword,
}: ScenarioDetailContentProps) {
  const { t } = useTranslate();
  const language = useSystemLanguageValue();
  const queryClient = useQueryClient();
  const feedbackModal = useModal<FeedbackMeta>();
  const receptionModeDrawer = useModal<ReceptionModeMeta>();
  const contentRef = useRef<HTMLDivElement>(null);
  const [openBranchPolicyId, setOpenBranchPolicyId] = useState('');
  const [togglingPolicyIds, setTogglingPolicyIds] = useState<string[]>([]);
  const resolveTodoTypeLabel = useResolveTodoTypeLabel();

  const scenarioDetailQuery = useQuery({
    ...pureLLM.scenarioDetail({
      region,
      storeId,
      storeGroupId,
      keyword,
      language,
      intentId: intentId ?? '',
    }),
    enabled: enabled && !!intentId,
  });

  const scenario = scenarioDetailQuery.data;

  const contentParameterQuery = pureLLM.contentParameterList({ language });
  const { data: contentParameters } = useQuery({
    ...contentParameterQuery,
  });
  const parameterMap = useMemo(() => {
    if (!contentParameters?.length) return undefined;
    const map: Record<string, (typeof contentParameters)[number]> = {};
    for (const p of contentParameters) {
      map[p.name] = p;
    }
    return map;
  }, [contentParameters]);

  const branchCards = useMemo(() => {
    if (!scenario) return [];
    return mapPolicies(scenario, resolveTodoTypeLabel);
  }, [scenario, resolveTodoTypeLabel]);

  const targetId = subPolicyId || policyId;

  const highlightPath = useMemo(() => {
    if (!targetId || !branchCards.length) return null;
    return findBranchPathByPolicyId(branchCards, targetId);
  }, [branchCards, targetId]);

  console.log('highlightPath', highlightPath, subPolicyId, policyId, targetId, branchCards);
  const highlightPolicyId = highlightPath?.[highlightPath.length - 1]?.policyId;

  useLayoutEffect(() => {
    if (branchCards.length) {
      setOpenBranchPolicyId(highlightPath?.[0]?.policyId ?? branchCards[0]?.policyId ?? '');
    }
  }, [branchCards, highlightPath]);

  useLayoutEffect(() => {
    if (!highlightPolicyId) return;

    let retryCount = 0;
    const maxRetryCount = 12;

    const scrollToTargetBranch = () => {
      const contentElement = contentRef.current;
      const targetBranch = contentElement?.querySelector<HTMLElement>(
        `[data-branch-policy-id="${highlightPolicyId}"]`,
      );
      const scrollContainer = contentElement ? getScrollParent(contentElement) : null;

      if (!targetBranch || !scrollContainer) {
        retryCount += 1;
        if (retryCount < maxRetryCount) {
          window.setTimeout(scrollToTargetBranch, 60);
        }
        return;
      }

      const targetOffsetTop = getOffsetTopWithinContainer(targetBranch, scrollContainer);
      const nextScrollTop =
        targetOffsetTop - scrollContainer.clientHeight / 2 + targetBranch.offsetHeight / 2;

      scrollContainer.scrollTop = Math.max(nextScrollTop, 0);
    };

    const timerId = window.setTimeout(scrollToTargetBranch, 80);

    return () => window.clearTimeout(timerId);
  }, [highlightPolicyId, openBranchPolicyId]);

  const policyToggleMutation = useMutation({
    mutationFn: ({ policyId: mutatePolicyId, enable }: { policyId: string; enable: boolean }) =>
      updateOfficialScenarioPolicy({
        region: region ?? '',
        storeGroupId: normalizeScenarioId(storeGroupId),
        policyId: normalizeScenarioId(mutatePolicyId),
        enable,
      }),
    onMutate: async ({ policyId: mutatePolicyId }) => {
      setTogglingPolicyIds((prev) =>
        prev.includes(mutatePolicyId) ? prev : [...prev, mutatePolicyId],
      );
    },
    onError: () => {
      message.error(t('scene.aiAgentConfig.feedback.operationFailed'));
    },
    onSuccess: () => {
      message.success(t('scene.aiAgentConfig.feedback.operationSuccess'));
      queryClient.invalidateQueries({
        queryKey: pureLLM.scenarioDetail({
          region,
          storeId,
          storeGroupId,
          language,
          intentId: intentId ?? '',
        }).queryKey,
      });
    },
    onSettled: (_data, _error, variables) => {
      setTogglingPolicyIds((prev) => prev.filter((id) => id !== variables.policyId));
    },
  });

  const receptionModeMutation = useMutation({
    mutationFn: ({
      subPolicyId: mutateSubPolicyId,
      mode,
      todoType,
    }: {
      subPolicyId: string;
      mode: NonNullable<ReceptionModeMeta['currentMode']>;
      todoType?: string;
    }) =>
      updateOfficialScenarioSubPolicy({
        region: region ?? '',
        storeGroupId: normalizeScenarioId(storeGroupId),
        subPolicyId: normalizeScenarioId(mutateSubPolicyId),
        receptionMode: mode,
        todoType: todoType ?? '',
      }),
    onSuccess: () => {
      message.success(t('scene.aiAgentConfig.feedback.operationSuccess'));
      queryClient.invalidateQueries({
        queryKey: pureLLM.scenarioDetail({
          region,
          storeId,
          storeGroupId,
          language,
          intentId: intentId ?? '',
        }).queryKey,
      });
    },
    onError: () => {
      message.error(t('scene.aiAgentConfig.feedback.operationFailed'));
    },
  });

  const branchCardsWithActions = useMemo(() => {
    const attachBranchCardActions = (item: BranchCardData): BranchCardData => ({
      ...item,
      playHitHighlight: targetId ? item.policyId === highlightPolicyId : false,
      onFeedbackClick: item.replyExample
        ? () =>
            feedbackModal.show({
              presetReply: item.replyExample,
            })
        : undefined,
      onModifyReceptionMode: item.receptionMode
        ? () =>
            receptionModeDrawer.show({
              subPolicyId: item.togglePolicyId,
              branchName: item.title,
              triggerConditions: item.policyTriggerConditions ?? item.triggerConditions,
              replyLogicDesc: item.replyLogicDesc,
              replyExample: item.replyExample,
              fixedReplyContent: item.fixedReplyContent,
              isFixedReply: item.isFixedReply || false,
              currentMode: item.receptionMode!,
              originalReceptionMode: item.originalReceptionMode,
              todoLabel: item.todoLabel,
              todoType: item.todoType,
              storeScope: item.storeScope,
            })
        : undefined,
      onToggle: item.policyId
        ? (enable) =>
            policyToggleMutation.mutate({
              policyId: String(item.policyId),
              enable,
            })
        : undefined,
      toggleLoading: item.policyId ? togglingPolicyIds.includes(String(item.policyId)) : false,
      children: item.children?.map(attachBranchCardActions),
    });

    return branchCards.map(attachBranchCardActions);
  }, [
    branchCards,
    targetId,
    highlightPolicyId,
    feedbackModal,
    receptionModeDrawer,
    policyToggleMutation,
    togglingPolicyIds,
  ]);

  const headerData = useMemo(() => {
    if (!scenario) return undefined;
    return {
      category: scenario.groupName,
      name: scenario.name,
      updateTime: scenario.updateTime,
      suggestedQuestions: scenario.example ?? [],
    };
  }, [scenario]);

  const ruleStatusBarData = useMemo(() => {
    if (!scenario) return undefined;
    return {
      enabledCount: Number(scenario.enablePolicyCount),
      totalCount: Number(scenario.policyCount),
    };
  }, [scenario]);

  if (scenarioDetailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spin />
      </div>
    );
  }

  if (!headerData || !ruleStatusBarData) return null;

  return (
    <div style={{ padding: 16 }}>
      <DetailHeader {...headerData} hideTitleBar={hideDetailTitleBar} />
      <div ref={contentRef} className="bg-[#fcfcfc]">
        <RuleStatusBar {...ruleStatusBarData} />
        <div>
          {branchCardsWithActions.map((item) => (
            <BranchCard
              key={`${item.policyId}-${item.index}`}
              {...item}
              collapsible
              expanded={openBranchPolicyId === item.policyId}
              listHorizontalPadding
              highlightBleedX={16}
              editable={editable}
              parameterMap={parameterMap}
              onExpandedChange={(next) => {
                if (next) {
                  setOpenBranchPolicyId(item.policyId);
                  return;
                }
                if (openBranchPolicyId === item.policyId) {
                  setOpenBranchPolicyId('');
                }
              }}
            />
          ))}
        </div>
      </div>

      <FeedbackModal
        visible={feedbackModal.visible}
        onClose={feedbackModal.hide}
        presetReply={feedbackModal.meta?.presetReply || ''}
      />

      <ReceptionModeDrawer
        visible={receptionModeDrawer.visible}
        onClose={receptionModeDrawer.hide}
        branchName={receptionModeDrawer.meta?.branchName || ''}
        triggerConditions={receptionModeDrawer.meta?.triggerConditions || []}
        replyLogicDesc={receptionModeDrawer.meta?.replyLogicDesc || ''}
        replyExample={receptionModeDrawer.meta?.replyExample || ''}
        fixedReplyContent={receptionModeDrawer.meta?.fixedReplyContent}
        isFixedReply={receptionModeDrawer.meta?.isFixedReply}
        currentMode={receptionModeDrawer.meta?.currentMode || 'AI_REPLY'}
        originalReceptionMode={receptionModeDrawer.meta?.originalReceptionMode}
        currentTodoType={receptionModeDrawer.meta?.todoType}
        storeGroupName={storeGroupName}
        storeScope={receptionModeDrawer.meta?.storeScope}
        zIndex={zIndex}
        onSave={async (values) => {
          const subPolicyId = receptionModeDrawer.meta?.subPolicyId;
          if (!subPolicyId) {
            throw new Error('missing subPolicyId');
          }
          await receptionModeMutation.mutateAsync({
            subPolicyId,
            mode: values.mode,
            todoType: values.todoType,
          });
        }}
        parameterMap={parameterMap}
      />
    </div>
  );
}

export interface ScenarioDetailModalProps {
  visible: boolean;
  onClose: () => void;
  editable?: boolean;
  region?: string;
  storeId?: string;
  storeGroupId?: string;
  storeGroupName?: string;
  intentId?: string;
  policyId?: string;
  subPolicyId?: string;
}

function ScenarioDetailModal(props: ScenarioDetailModalProps) {
  const {
    visible,
    onClose,
    editable,
    region,
    storeId,
    storeGroupId,
    storeGroupName,
    intentId,
    policyId,
    subPolicyId,
  } = props;

  return (
    <Modal
      data-sid="sdm-modal-01"
      visible={visible}
      title={
        <ScenarioDetailHeader
          enabled={visible}
          region={region}
          storeId={storeId}
          storeGroupId={storeGroupId}
          intentId={intentId}
        />
      }
      footer={null}
      onCancel={onClose}
      width={760}
      destroyOnClose
      bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: 0 }}
    >
      <ScenarioDetailContent
        enabled={visible && !!intentId}
        region={region}
        storeId={storeId}
        storeGroupId={storeGroupId}
        storeGroupName={storeGroupName}
        intentId={intentId}
        policyId={policyId}
        subPolicyId={subPolicyId}
        zIndex={1100}
        editable={editable}
      />
    </Modal>
  );
}

export default ScenarioDetailModal;
